import useSignout from "api/mutations/useSignout";
import { useGetPerson } from "api/queries/useGetPerson";
import { partialAccountFieldsFragment } from "gql/graphql";
import { MouseEvent, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getAccountTargetUrl as buildAccountTargetUrl } from "utils/getAccountTargetUrl";
import { sanitizeReturnUrl } from "utils/sanitizeReturnUrl";
import useRequiresSignin from "utils/useRequiresSignin";
import useSnackbar from "utils/useSnackbar";

const useController = () => {
	useRequiresSignin();

	const { data: person } = useGetPerson();
	const { mutateAsync: signout } = useSignout();

	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const sanitizedReturnUrl = sanitizeReturnUrl(searchParams.get("return_url"));
	const returnUrlParam = sanitizedReturnUrl?.url ?? null;
	const { handleError } = useSnackbar();

	const getAccountTargetUrl = useCallback(
		(accountIndex: number) =>
			buildAccountTargetUrl(accountIndex, returnUrlParam),
		[returnUrlParam]
	);

	const handleAccountSelect = (
		e: MouseEvent<HTMLButtonElement>,
		partialAccount: partialAccountFieldsFragment
	) => {
		e.preventDefault();
		const targetUrl = getAccountTargetUrl(partialAccount.index);
		if (person?.mfaAuthenticated || !partialAccount.mfaRequired) {
			if (sanitizedReturnUrl?.isAbsolute) {
				window.location.assign(targetUrl);
			} else if (targetUrl.startsWith("/u/")) {
				window.location.assign(`${window.location.origin}${targetUrl}`);
			} else {
				navigate(targetUrl);
			}
		} else {
			navigate(`/authenticate?return_url=${encodeURIComponent(targetUrl)}`);
		}
	};

	const handleSignout = () => {
		signout()
			.then((variables) => {
				if (variables.success) {
					navigate("/signin");
				} else if (variables.error) {
					handleError(variables.error);
				}
			})
			.catch((error) => handleError(error));
	};

	return { handleAccountSelect, handleSignout, person };
};

export default useController;
